import * as dateFnsTz from 'date-fns-tz'
import { tzOffset, tzScan } from '@date-fns/tz'

import { Timezone } from '@/entities'

export const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone as Timezone

export let userTimezone = browserTimezone

export const setUserTimezone = (timezone: Timezone | null) => {
    userTimezone = timezone || browserTimezone
}

export const canonicalizeTimezone = (timezone: Timezone): Timezone =>
    Intl.DateTimeFormat(undefined, { timeZone: timezone }).resolvedOptions().timeZone as Timezone

export const haveEquivalentTimezoneBehavior = (
    firstTimezone: Timezone,
    secondTimezone: Timezone,
    referenceDate = new Date()
): boolean => {
    if (firstTimezone === secondTimezone) {
        return true
    }

    const start = new Date(Date.UTC(referenceDate.getUTCFullYear(), 0, 1))
    const end = new Date(Date.UTC(referenceDate.getUTCFullYear() + 2, 0, 1))

    if (tzOffset(firstTimezone, start) !== tzOffset(secondTimezone, start)) {
        return false
    }

    const firstTransitions = tzScan(firstTimezone, { start, end })
    const secondTransitions = tzScan(secondTimezone, { start, end })

    return (
        firstTransitions.length === secondTransitions.length &&
        firstTransitions.every((transition, index) => {
            const secondTransition = secondTransitions[index]

            return (
                transition.date.getTime() === secondTransition?.date.getTime() &&
                transition.change === secondTransition.change &&
                transition.offset === secondTransition.offset
            )
        })
    )
}

export const getTimezoneOffset = (date: Date, timezone: Timezone): number => {
    return dateFnsTz.getTimezoneOffset(timezone, date)
}

export const getTimezoneOffsetDifference = (date1: Date, date2: Date, timezone: Timezone): number => {
    const date1Offset = getTimezoneOffset(date1, timezone)
    const date2Offset = getTimezoneOffset(date2, timezone)
    return date2Offset - date1Offset
}

export const getGMTOffset = (date: Date, timezone: Timezone) => {
    const offsetMilliseconds = getTimezoneOffset(date, timezone)

    const totalMinutes = offsetMilliseconds / 60_000

    const sign = offsetMilliseconds >= 0 ? '+' : '-'
    const hours = Math.abs(Math.floor(totalMinutes / 60))
    const minutes = Math.abs(totalMinutes % 60)

    return `GMT${sign}${minutes ? hours.toString().padStart(2, '0') : hours}${
        minutes ? `:${minutes.toString().padStart(2, '0')}` : ''
    }`
}
